import React, { useState, useEffect } from "react";
import * as d3 from "d3";

export function AircraftAnalyticsChart({ data }) {
  // State for selected ICAO filter
  const [selectedIcao, setSelectedIcao] = useState("All");

  useEffect(() => {
    // ===== 1. SET UP CHART DIMENSIONS =====
    const container = d3.select("#aircraft-chart");
    if (!container.node()) return;

    const containerWidth = container.node().getBoundingClientRect().width;
    const fullHeight = 400;
    const margin = { top: 20, right: 30, bottom: 50, left: 60 };
    const width = containerWidth - margin.left - margin.right;
    const height = fullHeight - margin.top - margin.bottom;

    // Clear any existing content
    container.selectAll("*").remove();

    // Create SVG
    const svg = container
      .append("svg")
      .attr("width", containerWidth)
      .attr("height", fullHeight)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // ===== 2. FILTER DATA BASED ON SELECTED ICAO =====
    let filteredData = data;
    if (selectedIcao !== "All") {
      filteredData = data.filter((d) =>
        d.aircraft_icao
          .split(",")
          .map((c) => c.trim())
          .includes(selectedIcao)
      );
    }

    // ===== 3. AGGREGATE DATA INTO 24 HOURS =====
    // For each hour:
    //   - Sum the beats_since_last_ping (sumBeats)
    //   - Collect a count per individual ICAO (icaoCountMap)
    //   - Convert icaoCountMap into an array of strings like ["C06921 (2)", "C09634 (1)", ...]
    const hourData = d3.range(24).map((hour) => {
      const items = filteredData.filter((d) => {
        const date = new Date(d.analytics_date);
        return date.getHours() === hour;
      });

      if (items.length > 0) {
        const sumBeats = d3.sum(items, (d) => d.beats_since_last_ping);

        // Build a map of { ICAO_code: count }
        const icaoCountMap = {};
        items.forEach((row) => {
          row.aircraft_icao.split(",").forEach((code) => {
            const trimmedCode = code.trim();
            icaoCountMap[trimmedCode] = (icaoCountMap[trimmedCode] || 0) + 1;
          });
        });

        // Convert the dictionary into an array of strings like "C06921 (2)"
        const icaoInfoArray = Object.entries(icaoCountMap).map(
        //   ([icaoCode, count]) => `${icaoCode} (${count})`
          ([icaoCode, count]) => `${icaoCode}`
        );

        return {
          hour,
          sumBeats,
          icaoInfo: icaoInfoArray, // store the array of "ICAO (count)"
          hasData: true,
        };
      }

      return {
        hour,
        sumBeats: 0,
        icaoInfo: [],
        hasData: false,
      };
    });

    // ===== 4. DETERMINE Y SCALE =====
    const maxBeats = d3.max(hourData, (d) => d.sumBeats) || 0;

    // X scale for hours [0..23]
    const x = d3
      .scaleBand()
      .domain(hourData.map((d) => d.hour))
      .range([0, width])
      .padding(0.1);

    // Y scale for sum of beats
    const y = d3
      .scaleLinear()
      .domain([0, maxBeats])
      .range([height, 0]);

    // ===== 5. DRAW AXES =====
    // X-axis
    const xAxis = d3.axisBottom(x).tickFormat((d) => `${d}:00`);
    svg
      .append("g")
      .attr("transform", `translate(0, ${height})`)
      .call(xAxis);

    // Y-axis
    const yAxis = d3.axisLeft(y).ticks(5);
    svg.append("g").call(yAxis);

    // Axis labels
    // X-axis label
    svg
      .append("text")
      .attr("x", width / 2)
      .attr("y", height + margin.bottom - 10)
      .attr("text-anchor", "middle")
      .style("font-weight", "bold")
      .text("Hour of the day");

    // Y-axis label
    svg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -margin.left + 15)
      .attr("x", -height / 2)
      .attr("text-anchor", "middle")
      .style("font-weight", "bold")
      .text("Number of beats since last ping");

    // ===== 6. TOOLTIP =====
    const tooltip = container
      .append("div")
      // Using 'fixed' or 'absolute' – either can work,
      // but 'fixed' can avoid scrolling issues if the container is large.
      .style("position", "fixed")
      .style("visibility", "hidden")
      .style("background", "white")
      .style("border", "1px solid #ececec")
      .style("padding", "8px")
      .style("border-radius", "5px")
      .style("font-size", "12px")
      .style("box-shadow", "0px 0px 4px rgba(0,0,0,0.2)")
      .style("max-width", "200px")
      .style("overflow", "auto");

    // ===== 7. DRAW BARS =====
    svg
      .selectAll(".bar")
      .data(hourData)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", (d) => x(d.hour))
      // If hasData is false, fill the entire chart height
      .attr("y", (d) => (d.hasData ? y(d.sumBeats) : 0))
      .attr("width", x.bandwidth())
      .attr("height", (d) => (d.hasData ? height - y(d.sumBeats) : height))
      .attr("fill", (d) => (d.hasData ? "#eab308" : "#ececec"))
      .on("mouseover", (event, d) => {
        if (d.hasData) {
          // Join all "ICAO (count)" strings with commas
          const icaoList = d.icaoInfo.join("<br/>");
          tooltip
            .html(
              `
                <strong>Hour:</strong> ${d.hour}:00<br/>
                <strong>Aircrafts:</strong> ${icaoList}<br/>
                <strong>Sum of beats:</strong> ${d.sumBeats}
              `
            )
            .style("visibility", "visible");
        } else {
          tooltip
            .html(`<strong>Hour:</strong> ${d.hour}:00<br/>No data`)
            .style("visibility", "visible");
        }
      })
      .on("mousemove", (event) => {
        tooltip
          .style("top", `${event.pageY - 30}px`)
          .style("left", `${event.pageX + 10}px`);
      })
      .on("mouseout", () => {
        tooltip.style("visibility", "hidden");
      });
  }, [data, selectedIcao]);

  // ===== 8. COLLECT UNIQUE ICAOs FOR THE DROPDOWN =====
  // Flatten all ICAOs from each row. Then make a unique set.
  const icaoSet = new Set();
  data.forEach((d) => {
    d.aircraft_icao.split(",").forEach((code) => {
      icaoSet.add(code.trim());
    });
  });
  const icaoList = Array.from(icaoSet).sort();

  return (
    <div >
      {/* Filter dropdown */}
      <div className="flex items-center mb-4">
        <label htmlFor="icaoFilter" className="mr-2 font-medium">
          Filter by ICAO:
        </label>
        <select
          id="icaoFilter"
          className="border px-3 py-2 rounded-md"
          value={selectedIcao}
          onChange={(e) => setSelectedIcao(e.target.value)}
        >
          <option value="All">All</option>
          {icaoList.map((icao) => (
            <option key={icao} value={icao}>
              {icao}
            </option>
          ))}
        </select>
      </div>

      {/* Chart container */}
      <div
        id="aircraft-chart"
        className="relative w-full"
        style={{ minHeight: "400px" }}
      />
    </div>
  );
}
